/**
 * CRO-Labs bundle packages, product picker and cart progress.
 *
 * Theme-owned replacement for Kaching Bundles + Kaching Cart. Plain custom
 * elements, no theme-framework imports, so the same file runs on Horizon,
 * Debutify and Concept. Every cart-update event those themes emit is observed.
 *
 * IMPORTANT: this only DISPLAYS prices and decides which line items are added.
 * Shopify charges whatever the automatic discounts in the admin say. Tier
 * prices configured in the theme editor must match those discounts.
 */

/* ------------------------------------------------------------------ *
 * Shared cart store: one fetch feeds every progress bar on the page
 * ------------------------------------------------------------------ */

const CART_EVENTS = ['cart:update', 'shopify:cart:lines-update', 'cart:refresh', 'cart:change'];

const cartStore = {
  subscribers: new Set(),
  cart: null,
  pending: null,

  subscribe(fn) {
    this.subscribers.add(fn);
    // Paint the cached cart first so there is no flash of an empty bar, then
    // always re-read from the network. A bar that mounts inside a freshly
    // re-rendered cart drawer would otherwise show the pre-change cart.
    if (this.cart) fn(this.cart);
    this.refresh();
    return () => this.subscribers.delete(fn);
  },

  /**
   * Read the cart.
   *
   * Every request carries a ticket number and only the newest one is allowed to
   * publish. Two adds in quick succession fire two reads, and the browser is
   * free to finish them out of order: without this the older answer lands last
   * and the bar drops back to the previous count while the cart is already
   * ahead. Cheap to fire an extra read, expensive to show the wrong number.
   */
  refresh({ force = true } = {}) {
    if (this.pending && !force) return this.pending;
    const ticket = (this.ticket || 0) + 1;
    this.ticket = ticket;
    const root = window.Shopify?.routes?.root || '/';
    const request = fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`cart.js ${res.status}`))))
      .then((cart) => {
        if (ticket !== this.ticket) return this.cart; // a newer read already won
        this.cart = cart;
        this.subscribers.forEach((fn) => fn(cart));
        return cart;
      })
      .catch(() => null)
      .finally(() => {
        if (this.pending === request) this.pending = null;
      });
    this.pending = request;
    return request;
  },
};

CART_EVENTS.forEach((name) => {
  const onChange = (event) => {
    projection.set(0, 0);
    // Our own announcement carries the cart we just read, so re-fetching here
    // would fire one /cart.js per event name for nothing.
    if (event?.detail?.cro) return;

    // The theme fires this the moment it STARTS writing, not when it is done.
    // Reading straight away races the write, and on two quick removals the read
    // comes back with the cart as it was one step earlier. Horizon hands us a
    // promise that settles once the write landed, so wait for it when it is
    // there and read after.
    const written = event?.promise;
    if (written && typeof written.then === 'function') {
      written.then(() => cartStore.refresh()).catch(() => cartStore.refresh());
      return;
    }
    cartStore.refresh();
  };
  document.addEventListener(name, onChange);
  window.addEventListener(name, onChange);
});

/**
 * What the tier the shopper has selected would add if they hit add-to-cart.
 * The product-page ladder shows cart + this, so choosing "3 books" moves the bar
 * immediately instead of waiting for the add. The drawer ignores it, because
 * there the cart is the truth.
 */
const projection = {
  units: 0,
  amount: 0,
  listeners: new Set(),
  set(units, amount) {
    this.units = units;
    this.amount = amount;
    this.listeners.forEach((fn) => fn());
  },
  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  },
};

/**
 * Sections the cart API should re-render for us: the drawer, nothing else.
 * Section ids differ per theme (Horizon, Concept and Debutify all name them
 * differently), so read them off the page rather than hard coding one store's.
 * The header is deliberately NOT re-rendered: swapping it out costs Horizon its
 * modal cart dialog, so the drawer stops closing on an outside click. The cart
 * badge is kept in step by announceCartUpdate() instead.
 */
function cartSectionIds() {
  const ids = new Set();
  document
    .querySelectorAll('#cart-drawer, cart-drawer, cart-drawer-component')
    .forEach((el) => {
      const section = el.closest('[id^="shopify-section-"]');
      if (section) ids.add(section.id.replace('shopify-section-', ''));
    });
  return [...ids];
}

/**
 * Tell the theme the cart changed. Horizon's cart icon listens for
 * `shopify:cart:lines-update` and reads `event.promise`, so an event without
 * one is silently ignored and the header badge keeps the old count.
 */
function announceCartUpdate(cart) {
  if (!cart) return;
  const detail = { itemCount: cart.item_count ?? 0, cro: true };
  CART_EVENTS.forEach((name) => {
    const event = new CustomEvent(name, { bubbles: true, detail });
    event.promise = Promise.resolve({ cart: { totalQuantity: detail.itemCount }, detail });
    document.dispatchEvent(event);
  });
}

/** Swap in freshly rendered cart markup returned by the cart API. */
function renderCartSections(sections) {
  if (!sections) return;
  Object.entries(sections).forEach(([id, html]) => {
    if (!html) return;
    const target = document.getElementById(`shopify-section-${id}`);
    if (!target) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = parsed.getElementById(`shopify-section-${id}`);
    if (!fresh) return;

    // Replacing the whole drawer section throws away the <dialog> element and
    // with it the open state, so adding a cross-sell from inside the drawer
    // slammed it shut. Swap only the cart contents and leave the dialog alone.
    const liveBody = target.querySelector('dialog > *, dialog cart-drawer-component');
    const freshBody = fresh.querySelector('dialog > *, dialog cart-drawer-component');
    if (liveBody && freshBody) {
      liveBody.replaceWith(freshBody);
      return;
    }
    target.replaceWith(fresh);
  });
}

/* ------------------------------------------------------------------ *
 * Money
 * ------------------------------------------------------------------ */

let MONEY_FORMAT = '';

/** Thousands grouping with the given separator: "1234567" -> "1.234.567". */
const group = (digits, sep) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, sep);

/**
 * Format cents the way Shopify's `money` filter does for the store's format.
 * All eight `{{amount...}}` tokens are covered, so a Nordic, Polish, Czech or
 * Hungarian store keeps its "1 234,56" after the first JS repaint instead of
 * flipping to the English pattern. The no-decimal tokens round to the unit,
 * as Shopify does (1,134.65 renders as 1,135).
 */
function money(cents) {
  const rounded = Math.round(cents);
  const value = (rounded / 100).toFixed(2);
  if (!MONEY_FORMAT) return value;
  const [whole, dec] = value.split('.');
  const units = String(Math.round(rounded / 100));
  return MONEY_FORMAT.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
    switch (token) {
      case 'amount_no_decimals':
        return group(units, ',');
      case 'amount_with_comma_separator':
        return `${group(whole, '.')},${dec}`;
      case 'amount_no_decimals_with_comma_separator':
        return group(units, '.');
      case 'amount_with_space_separator':
        return `${group(whole, ' ')},${dec}`;
      case 'amount_no_decimals_with_space_separator':
        return group(units, ' ');
      case 'amount_with_apostrophe_separator':
        return `${group(whole, "'")}.${dec}`;
      case 'amount_with_period_and_space_separator':
        return `${group(whole, ' ')}.${dec}`;
      default:
        return `${group(whole, ',')}.${dec}`;
    }
  });
}

/**
 * Rendered price text back into cents, whatever the store's format. Everything
 * that is not a digit or a separator is dropped first (currency symbol or code,
 * spaces, apostrophes), which leaves one question: which "." or "," is the
 * decimal mark. It is the last one when one or two digits follow it. A group of
 * three is a thousands separator, and the no-decimal formats have no tail.
 */
function parseMoney(text) {
  const raw = String(text)
    .replace(/[^\d.,]/g, '')
    .replace(/^[.,]+|[.,]+$/g, '');
  if (!raw) return 0;
  const match = /^(.*?)(?:[.,](\d{1,2}))?$/.exec(raw);
  const whole = Number(match[1].replace(/\D/g, '')) || 0;
  const dec = Number((match[2] || '').padEnd(2, '0')) || 0;
  return whole * 100 + dec;
}

/**
 * Snap a computed price to a charm ending (.99, .95, .00).
 * Percentage tiers recompute whenever the merchant changes a product price, so
 * without this they land on values like 42.55. Rounds down so the shown price is
 * never higher than the percentage promises.
 */
function roundCharm(cents, ending) {
  if (!ending || ending === 'none') return cents;
  const target = { '99': 99, '95': 95, '00': 0 }[ending];
  if (target === undefined) return cents;
  const whole = Math.floor(cents / 100);
  const above = whole * 100 + target;
  const below = (whole - 1) * 100 + target;
  const nearest = Math.abs(above - cents) <= Math.abs(cents - below) ? above : below;
  return Math.max(0, nearest);
}

const parseJSON = (raw, fallback) => {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

/* ------------------------------------------------------------------ *
 * <cro-packages>
 * ------------------------------------------------------------------ */

class CroPackages extends HTMLElement {
  connectedCallback() {
    MONEY_FORMAT = this.dataset.croMoneyFormat || MONEY_FORMAT;
    this.variants = parseJSON(this.dataset.croVariantMap, {});
    this.tiers = [...this.querySelectorAll('[data-cro-tier]')];
    if (!this.tiers.length) return;

    this.form = this.closest('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');

    // One selection for the whole block, shared by every tier.
    this.picks = this.picks || new Map();
    this.wired = this.wired || new WeakSet();

    // Combined layout: one card, the shopper just picks books and the tier is
    // derived from how many they picked. The other tiers stay in the template
    // as the price ladder, they simply are not rendered as cards.
    this.combined = this.classList.contains('cro-packages--combined');

    this.tiers.forEach((tier) => {
      // Guarded against binding twice when a theme re-inserts the section.
      // Held on the instance, not as an attribute: an attribute travels with
      // cloned or cached markup and would leave the fresh copy with no
      // listeners at all.
      if (this.wired.has(tier)) return;
      this.wired.add(tier);
      const route = (event) => {
        const step = event.target.closest('[data-cro-step]');
        if (step) {
          event.stopPropagation();
          this.select(tier);
          this.step(step.closest('[data-cro-option]'), Number(step.dataset.croStep));
          return;
        }
        // A chip in the chosen row takes that one book back out.
        const chip = event.target.closest('[data-cro-chip]');
        if (chip) {
          event.stopPropagation();
          this.select(tier);
          this.step(tier.querySelector(`[data-cro-option="${chip.dataset.croChip}"]`), -1);
          return;
        }
        // Anywhere else on a book card counts as one more of it. Two clicks on
        // Papa is the common "I want two of the same" case, and the stepper
        // underneath shows what happened.
        const card = event.target.closest('[data-cro-option]');
        if (card) {
          event.stopPropagation();
          this.select(tier);
          this.step(card, 1);
          return;
        }
        // Clicking the tier card itself is a preset: it sets the selection to
        // that many books. Trimming here is expected, unlike the stepper
        // silently pushing a book out from under the shopper.
        this.select(tier);
        this.applyTierPreset(tier);
      };
      tier.addEventListener('click', route);
      tier.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        route(event);
      });
    });

    // Put the product being viewed into the first slot of every picker tier.
    // The shopper is already on that book, so making them choose it again is
    // a pointless click.
    if (this.dataset.croPreselectCurrent === 'true') this.preselectCurrent();
    this.renderPicks();

    if (this.combined) {
      this.card = this.tiers[this.tiers.length - 1];
      this.select(this.card, { silent: true });
    } else {
      this.select(this.tiers.find((t) => t.dataset.croPreselected === 'true') || this.tiers[0], { silent: true });
    }

    // Keep prices honest when the shopper switches variant.
    this.onVariantChange = () => this.repriceAll();
    ['variant:update', 'shopify:product:variant-change', 'change'].forEach((name) =>
      document.addEventListener(name, this.onVariantChange)
    );

    // Horizon intercepts the add-to-cart CLICK, not the form's submit event, so
    // a capture listener on submit never runs early enough and both handlers
    // added the product. Catching the click in the capture phase is the only
    // point that reliably comes first.
    this.form?.addEventListener('click', this.onAddClick, { capture: true });
    this.form?.addEventListener('submit', this.onSubmit, { capture: true });
    this.repriceAll();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('click', this.onAddClick, { capture: true });
    this.form?.removeEventListener('submit', this.onSubmit, { capture: true });
    ['variant:update', 'shopify:product:variant-change', 'change'].forEach((name) =>
      document.removeEventListener(name, this.onVariantChange)
    );
  }

  /** The variant the page is currently showing. */
  currentVariantId() {
    return this.form?.querySelector('input[name="id"], select[name="id"]')?.value || this.dataset.croDefaultVariant;
  }

  /* ---------------- selection ---------------- */

  select(tier, { silent = false } = {}) {
    if (!tier || tier === this.current) return;
    this.current = tier;
    this.tiers.forEach((t) => {
      const active = t === tier;
      t.classList.toggle('cro-tier--selected', active);
      t.setAttribute('aria-checked', active ? 'true' : 'false');
      const panel = t.querySelector('[data-cro-tier-panel]');
      if (panel) panel.hidden = !active;
    });
    const input = this.form?.querySelector('input[name="quantity"]');
    if (input) input.value = this.config(tier).quantity || 1;
    // Every tier reprices: the one being left has to drop back to its own count.
    this.repriceAll();
    this.publishProjection();
    if (!silent) this.dispatchEvent(new CustomEvent('cro:tier-change', { bubbles: true }));
  }

  /* ---------------- picking ---------------- */

  /**
   * One shared selection for the whole block, keyed by variant id.
   *
   * It used to live on each tier's own cards, which meant a fixed tier had to
   * push a book out to stay within its count. Nobody wants a picker that
   * deletes what they already chose. Now the selection is the truth and the
   * tier follows it: take a third book on the two book tier and the three book
   * tier lights up instead.
   */
  pickedTotal() {
    let sum = 0;
    this.picks.forEach((n) => {
      sum += n;
    });
    return sum;
  }

  /** Which tier a given number of books belongs on. */
  tierForCount(count) {
    const pickers = this.tiers.filter((t) => this.config(t).mode === 'customer_picks');
    if (!pickers.length) return null;
    const exact = pickers.find((t) => (this.config(t).units || 1) === count);
    if (exact) return exact;
    const sorted = [...pickers].sort((a, b) => (this.config(a).units || 1) - (this.config(b).units || 1));
    if (count <= (this.config(sorted[0]).units || 1)) return sorted[0];
    return sorted[sorted.length - 1];
  }

  /** Change one book's count. Never touches any other book. */
  step(card, delta) {
    if (!card) return;
    const id = card.dataset.croOption;
    const next = Math.max(0, (this.picks.get(id) || 0) + delta);
    if (next === 0) this.picks.delete(id);
    else this.picks.set(id, next);

    // The tier only ever moves UP with the selection. Taking a book out to swap
    // it for another one must not drop the shopper from the four book offer to
    // the three book one: they are mid-edit, not downgrading. Going down is the
    // tier card's job, where it is an explicit choice.
    const cfg = this.config(this.current);
    const own = cfg.pickCount || cfg.units || 1;
    const total = this.pickedTotal();
    const target = this.combined || total <= own ? this.current : this.tierForCount(total) || this.current;

    this.renderPicks();
    if (target && target !== this.current) this.select(target);
    else {
      this.reprice(this.current);
      this.publishProjection();
    }
    this.current?.querySelector('[data-cro-pick-error]')?.setAttribute('hidden', '');
  }

  /**
   * Make the selection match a tier's count. Books already picked are kept,
   * the newest go first when trimming, and anything missing is filled with the
   * product on the page.
   */
  applyTierPreset(tier) {
    const cfg = this.config(tier);
    if (cfg.mode !== 'customer_picks' || this.combined) return;
    const want = cfg.pickCount || cfg.units || 1;

    while (this.pickedTotal() > want) {
      const ids = [...this.picks.keys()];
      const last = ids[ids.length - 1];
      if (!last) break;
      const n = this.picks.get(last);
      if (n > 1) this.picks.set(last, n - 1);
      else this.picks.delete(last);
    }

    // Nothing is filled in beyond the book the shopper is already looking at.
    // Choosing the rest is the whole point of the picker, and guessing on their
    // behalf only means they have to undo it first.
    if (!this.picks.size) {
      const id = String(this.currentVariantId() || '');
      if (id && tier.querySelector(`[data-cro-option="${id}"]`)) this.picks.set(id, 1);
    }

    this.renderPicks();
    this.repriceAll();
    this.publishProjection();
  }

  /** Put the product being viewed into the selection once, on first load. */
  preselectCurrent() {
    const id = String(this.currentVariantId() || '');
    if (!id || this.picks.size) return;
    const known = this.tiers.some((t) =>
      [...t.querySelectorAll('[data-cro-option]')].some((c) => c.dataset.croOption === id)
    );
    if (known) this.picks.set(id, 1);
  }

  /** Redraw every tier's steppers and chosen row from the shared selection. */
  renderPicks() {
    this.tiers.forEach((tier) => {
      tier.querySelectorAll('[data-cro-option]').forEach((card) => {
        const n = this.picks.get(card.dataset.croOption) || 0;
        // Mirrored onto the card so the DOM tells the truth when anyone
        // inspects it; the selection itself lives on the component.
        card.dataset.croQty = String(n);
        card.classList.toggle('cro-option--chosen', n > 0);
        const out = card.querySelector('[data-cro-count]');
        if (out) out.textContent = String(n);
        const down = card.querySelector('[data-cro-step="-1"]');
        if (down) down.disabled = n === 0;
      });
      this.renderChosen(tier);
    });
  }

  /**
   * The row of books actually taken, one chip each, plus empty chips for what
   * is still missing on a fixed tier. Clicking a chip takes that book back out.
   */
  renderChosen(tier) {
    const row = tier.querySelector('[data-cro-chosen]');
    if (!row) return;
    const cfg = this.config(tier);
    const own = cfg.pickCount || cfg.units || 1;
    const chips = [];

    this.picks.forEach((qty, id) => {
      const card = tier.querySelector(`[data-cro-option="${id}"]`);
      if (!card) return;
      const src = card.querySelector('img')?.src || '';
      const name = card.dataset.croTitle || '';
      for (let i = 0; i < qty; i += 1) {
        chips.push(
          `<span class="cro-chip" data-cro-chip="${id}" role="button" tabindex="0" title="${name}">` +
            `<img class="cro-chip__img" src="${src}" alt="" width="60" height="60">` +
            `<span class="cro-chip__name">${name}</span>` +
            '<span class="cro-chip__remove" aria-hidden="true">&times;</span>' +
            '</span>'
        );
      }
    });

    const missing = this.combined ? 0 : Math.max(0, own - chips.length);
    for (let i = 0; i < missing; i += 1) {
      chips.push(
        '<span class="cro-chip cro-chip--empty" aria-hidden="true">' +
          `<span class="cro-chip__name">${tier.dataset.croPickLabel || ''}</span>` +
          '</span>'
      );
    }
    row.innerHTML = chips.join('');
  }

  /* ---------------- pricing ---------------- */

  config(tier) {
    return parseJSON(tier.dataset.croConfig, {});
  }

  /**
   * Tell the ladder what the current selection would add. Called only from real
   * user actions, and cleared whenever the cart changes, so a tier that has
   * already been added is never counted a second time.
   */
  publishProjection() {
    const tier = this.current;
    if (!tier) return;
    const cfg = this.config(tier);
    // The bar reflects the tier the shopper has selected, not just how many
    // books they have clicked so far. Standing on "2 Libros" with one book
    // picked is a promise of two, and the ladder should already say -44%.
    const picked = this.pickedTotal();
    let units;
    if (cfg.mode !== 'customer_picks') units = Math.max(1, cfg.units || 1);
    else if (this.combined) units = Math.max(1, picked);
    else units = Math.max(cfg.pickCount || cfg.units || 1, picked, 1);
    const price = tier.querySelector('[data-cro-price]');
    // reprice() leaves the exact cents on the node. The text is the fallback
    // for markup nobody has repriced yet, and it is parsed format-agnostically.
    const cents = price ? (price.dataset.croCents ? Number(price.dataset.croCents) : parseMoney(price.textContent)) : 0;
    projection.set(units, Number.isFinite(cents) ? cents : 0);
  }

  /**
   * In combined mode the price comes from whichever configured tier matches the
   * number of books picked. One book picked is priced like the 1-book tier, two
   * like the 2-book tier, and so on, so the existing ladder stays the source of
   * truth and nothing is duplicated.
   */
  tierConfigForCount(count) {
    const configs = this.tiers.map((t) => this.config(t));
    const exact = configs.find((c) => (c.units || 1) === count);
    if (exact) return exact;
    // Below the first tier, or above the last, clamp to the nearest one.
    const sorted = [...configs].sort((a, b) => (a.units || 1) - (b.units || 1));
    if (!sorted.length) return {};
    if (count <= (sorted[0].units || 1)) return sorted[0];
    return sorted[sorted.length - 1];
  }

  /**
   * Recompute a tier's displayed price from live data rather than trusting what
   * Liquid rendered, so switching variant or picking products stays accurate.
   */
  reprice(tier) {
    let cfg = this.config(tier);
    // Combined has no fixed tiers, so the price comes from whichever rung of the
    // ladder matches what is picked. A fixed tier that is marked allowMore does
    // the same once the shopper goes past its own count: five books stay on the
    // top rung and the tier just counts up.
    // Only the selected tier reflects the live selection. The others keep
    // showing their own count, otherwise every card would read the same number.
    const picked = tier === this.current || this.combined ? this.pickedTotal() : 0;
    const floats = this.combined || (cfg.allowMore && picked > (cfg.pickCount || cfg.units || 1));
    // What the card is priced for: its own count, or more once the shopper has
    // gone past it on an open tier.
    const unitsShown = this.combined
      ? Math.max(1, picked)
      : Math.max(cfg.pickCount || cfg.units || 1, picked, 1);
    if (cfg.mode === 'customer_picks' && floats) {
      const n = Math.max(1, picked);
      cfg = { ...this.tierConfigForCount(n), mode: 'customer_picks', options: cfg.options, units: n, allowMore: cfg.allowMore, pickCount: cfg.pickCount };
    }
    const current = this.variants[this.currentVariantId()] || {};
    const basePrice = current.price ?? cfg.basePrice ?? 0;
    const baseCompare = Math.max(current.compare || 0, basePrice);

    let undiscounted = 0;
    let compare = 0;

    if (cfg.mode === 'specific') {
      undiscounted = basePrice;
      compare = baseCompare;
      (cfg.extras || []).forEach((e) => {
        undiscounted += e.price;
        compare += Math.max(e.compare || 0, e.price);
      });
    } else if (cfg.mode === 'customer_picks') {
      const counts = tier === this.current || this.combined ? this.picks : new Map();
      // A fixed tier always prices its own count, even before anything is
      // picked, so the card never reads as cheaper than it is. Whatever is
      // still unpicked is priced at the product on the page. Marking the tier
      // open ended must not drop that baseline to one book.
      const target = unitsShown;
      let counted = 0;
      counts.forEach((qty, id) => {
        const opt = (cfg.options || []).find((o) => String(o.id) === String(id));
        const price = opt ? opt.price : basePrice;
        undiscounted += price * qty;
        compare += (opt ? Math.max(opt.compare || 0, opt.price) : baseCompare) * qty;
        counted += qty;
      });
      for (let i = counted; i < target; i += 1) {
        undiscounted += basePrice;
        compare += baseCompare;
      }
    } else {
      const qty = cfg.quantity || 1;
      undiscounted = basePrice * qty;
      compare = baseCompare * qty;
    }

    let total = undiscounted;
    if (cfg.priceMode === 'fixed_total') {
      total = cfg.fixedTotal;
    } else if (cfg.priceMode === 'percent_off') {
      const base = cfg.percentBase === 'compare_at' ? compare : undiscounted;
      // Mirror how Shopify actually charges it: the discount amount is cut off
      // at whole cents, not rounded. Otherwise a 33% tier reads 13,39 on the
      // card and 13,40 in the cart, and the shopper notices the cent.
      total = base - Math.floor((base * (parseFloat(cfg.percentOff) || 0)) / 100);
      total = roundCharm(total, cfg.rounding);
    }

    // Combined card: the heading and the pill must say what the shopper has
    // actually picked, not the maximum tier. Otherwise it reads "4 Libros"
    // next to a two book price.
    // The title is rewritten on every reprice, not only when the tier is over
    // its own count. Otherwise a card that once read "6 Libros" keeps saying so
    // after the shopper drops back to four.
    if (cfg.mode === 'customer_picks' && cfg.titleTemplate?.includes('{count}')) {
      const titleEl = tier.querySelector('[data-cro-title-out]');
      // The trailing "+" marks the tier as open ended, so it stays put whatever
      // the count reads.
      // In the combined layout the count is already live, so a trailing "+"
      // just reads as a typo next to "1 Libro".
      const suffix = cfg.allowMore && !this.combined ? ' +' : '';
      if (titleEl) titleEl.textContent = cfg.titleTemplate.replace('{count}', unitsShown) + suffix;
    }
    if (floats) {
      const pillEl = tier.querySelector('[data-cro-pill]');
      if (pillEl) {
        pillEl.textContent = cfg.pill || '';
        pillEl.hidden = !cfg.pill || picked === 0;
      }
    }

    const saving = Math.max(0, compare - total);
    // Price per item, so a 3-book tier can show "17.99 each".
    const unitEl = tier.querySelector('[data-cro-unit]');
    if (unitEl?.dataset.croUnitTemplate) {
      const units = cfg.mode === 'customer_picks' ? unitsShown : Math.max(1, cfg.units || 1);
      unitEl.textContent = unitEl.dataset.croUnitTemplate.replace('{unit}', money(total / units));
    }

    const priceEl = tier.querySelector('[data-cro-price]');
    const compareEl = tier.querySelector('[data-cro-compare]');
    const labelEl = tier.querySelector('[data-cro-label]');

    if (priceEl) {
      priceEl.textContent = money(total);
      // Exact cents for the projection: a no-decimal format drops them from the text.
      priceEl.dataset.croCents = String(Math.round(total));
    }
    if (compareEl) {
      compareEl.textContent = money(compare);
      compareEl.hidden = compare <= total;
    }
    if (labelEl && labelEl.dataset.croLabelTemplate) {
      labelEl.textContent = labelEl.dataset.croLabelTemplate
        .replace('{saved}', money(saving))
        .replace('{percent}', compare > 0 ? `${Math.round((saving / compare) * 100)}%` : '0%');
    }
  }

  repriceAll() {
    this.tiers.forEach((tier) => this.reprice(tier));
  }

  /* ---------------- add to cart ---------------- */

  /** Intercept the add-to-cart button before the theme sees the click. */
  onAddClick = (event) => {
    const button = event.target.closest('button[name="add"], button[type="submit"], [type="submit"]');
    if (!button || !this.form?.contains(button)) return;
    if (this.lineItems() === null) return; // nothing special about this tier, let the theme add it
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.addSelection(button);
  };

  onSubmit = async (event) => {
    if (!this.current) return;
    const items = this.lineItems();
    if (items === null) return; // plain single item, let the theme handle it
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.addSelection(this.form.querySelector('[type="submit"]'));
  };

  async addSelection(button) {
    const items = this.lineItems();
    if (!items || !items.length) return;
    button?.setAttribute('aria-busy', 'true');
    try {
      const root = window.Shopify?.routes?.root || '/';
      // Ask Shopify to re-render the drawer section in the same request.
      // Adding through fetch bypasses the theme's own cart flow, so nothing
      // else tells the drawer to update and it keeps showing "cart is empty".
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: cartSectionIds().join(',') }),
      });
      if (!res.ok) throw new Error(await res.text());
      const payload = await res.json().catch(() => null);
      renderCartSections(payload?.sections);
      // The selection is now in the cart, so it is no longer pending.
      projection.set(0, 0);
      cartStore.refresh().then(announceCartUpdate);
      if (this.dataset.croOpenCart !== 'false') this.openCart();
    } catch (error) {
      console.error('[cro-packages] add to cart failed', error);
      this.form.submit();
    } finally {
      button?.removeAttribute('aria-busy');
    }
  }

  /**
   * Open the cart drawer. Horizon only auto-opens when the theme setting says
   * so, and that setting is off on these stores, so we open it ourselves.
   */
  openCart() {
    const dialog = document.querySelector('#cart-drawer dialog');
    if (dialog?.open) return;

    // Click the theme's own cart button rather than calling an API. Opening it
    // programmatically produces a NON modal dialog: no backdrop, and clicking
    // outside does not close it. The header button opens it modally, which is
    // the behaviour shoppers expect and what the theme does everywhere else.
    const trigger = document.querySelector('[aria-controls="cart-drawer"]');
    if (trigger) {
      trigger.click();
      return;
    }

    try {
      if (window.Shopify?.actions?.openCart) {
        window.Shopify.actions.openCart();
        return;
      }
    } catch (error) {
      /* fall through to the theme element */
    }
    const drawer = document.querySelector('#cart-drawer, cart-drawer, theme-drawer#cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  /** null means "not our business, let the theme submit normally". */
  lineItems() {
    const tier = this.current;
    const cfg = this.config(tier);
    const mainVariant = this.currentVariantId();

    if (cfg.mode === 'specific') {
      const ids = [mainVariant, ...(cfg.extras || []).map((e) => e.id)];
      return this.group(ids);
    }

    if (cfg.mode === 'customer_picks') {
      const total = this.pickedTotal();
      // A fixed tier has to be full: half a three book bundle at the three book
      // price is not something to let through.
      const needed = this.combined ? 1 : cfg.pickCount || cfg.units || 1;
      if (total < needed) {
        tier.querySelector('[data-cro-pick-error]')?.removeAttribute('hidden');
        return [];
      }
      return [...this.picks].map(([id, quantity]) => ({ id: Number(id), quantity }));
    }

    const qty = cfg.quantity || 1;
    if (!mainVariant) return null; // nothing selected on the page, let the theme deal with it
    return [{ id: Number(mainVariant), quantity: qty }];
  }

  group(ids) {
    const counts = ids.filter(Boolean).reduce((acc, id) => acc.set(String(id), (acc.get(String(id)) || 0) + 1), new Map());
    return [...counts].map(([id, quantity]) => ({ id: Number(id), quantity }));
  }
}

/* ------------------------------------------------------------------ *
 * <cro-progress>, shared across home page, PDP and cart drawer
 * ------------------------------------------------------------------ */

class CroProgress extends HTMLElement {
  connectedCallback() {
    MONEY_FORMAT = this.dataset.croMoneyFormat || MONEY_FORMAT;
    this.milestones = parseJSON(this.dataset.croMilestones, []);
    this.metric = this.dataset.croMetric === 'subtotal' ? 'subtotal' : 'quantity';
    this.project = this.dataset.croProject === 'true';
    if (!this.milestones.length) return;
    this.unsubscribe = cartStore.subscribe((cart) => this.render(cart));
    if (this.project) {
      this.unsubscribeProjection = projection.subscribe(() => this.render(cartStore.cart));
    }
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    this.unsubscribeProjection?.();
  }

  render(cart) {
    if (!cart) return;
    const inCart =
      this.metric === 'quantity'
        ? cart.item_count
        : cart.items.reduce((sum, item) => sum + item.original_line_price, 0);
    // The selection is only a preview while there is nothing in the cart yet.
    // Once the shopper has items, the bar tracks the cart alone: adding the
    // selection on top double counts the book they just added and pushes the
    // bar to the next tier for no reason.
    const previewable = this.project && cart.item_count === 0;
    const pending = previewable ? (this.metric === 'quantity' ? projection.units : projection.amount) : 0;
    const current = inCart + pending;

    const index = this.milestones.filter((m) => current >= m.threshold).length;
    const next = this.milestones[index];

    // Even visual spacing between steps regardless of threshold gaps.
    let ratio = 0;
    if (current > 0) {
      ratio = index / this.milestones.length;
      if (next) {
        const floor = index === 0 ? 0 : this.milestones[index - 1].threshold;
        const span = next.threshold - floor || 1;
        const into = Math.min(1, Math.max(0, (current - floor) / span));
        // Past the first rung the fill sits half a step ahead of the dot just
        // unlocked. Quantities are whole numbers, so otherwise the bar stops
        // dead on the dot and reads as stuck rather than "next one ahead".
        ratio += (index > 0 ? 0.5 + into / 2 : into) / this.milestones.length;
      }
    }
    // Re-queried every time: the theme swaps these nodes out from under us.
    const fill = this.querySelector('[data-cro-progress-fill]');
    if (fill) fill.style.width = `${Math.min(100, Math.max(0, ratio * 100))}%`;

    this.querySelectorAll('[data-cro-milestone]').forEach((node) => {
      node.classList.toggle('cro-progress__milestone--reached', current >= Number(node.dataset.croMilestone));
    });

    const message = this.querySelector('[data-cro-progress-message]');
    if (message) {
      if (!next) {
        message.textContent = (this.dataset.croComplete || '').replace(
          '{reward}',
          this.milestones[this.milestones.length - 1]?.label || ''
        );
      } else {
        const remaining = next.threshold - current;
        message.textContent = (this.dataset.croTemplate || '')
          .replace('{amount}', this.metric === 'quantity' ? remaining : money(remaining))
          .replace('{reward}', next.label || '');
      }
    }
    this.toggleAttribute('data-cro-empty', cart.item_count === 0);
  }
}

/* ------------------------------------------------------------------ *
 * <cro-countdown>
 * ------------------------------------------------------------------ */

class CroCountdown extends HTMLElement {
  connectedCallback() {
    this.template = this.dataset.croTemplate || '{timer}';
    this.mode = this.dataset.croMode || 'midnight';
    this.minutes = Number(this.dataset.croMinutes || 15);
    this.deadline = this.resolveDeadline();
    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  resolveDeadline() {
    if (this.mode === 'fixed_date' && this.dataset.croDate) {
      const parsed = Date.parse(this.dataset.croDate);
      if (!Number.isNaN(parsed)) return parsed;
    }
    if (this.mode === 'minutes') {
      // Per-visitor countdown, persisted so a reload does not restart it.
      const key = 'cro-countdown';
      const stored = Number(sessionStorage.getItem(key));
      if (stored && stored > Date.now()) return stored;
      const end = Date.now() + this.minutes * 60000;
      sessionStorage.setItem(key, String(end));
      return end;
    }
    const midnight = new Date();
    midnight.setHours(24, 0, 0, 0);
    return midnight.getTime();
  }

  tick() {
    let remaining = Math.max(0, this.deadline - Date.now());
    if (remaining === 0 && this.mode === 'midnight') {
      this.deadline = this.resolveDeadline();
      remaining = Math.max(0, this.deadline - Date.now());
    }
    const pad = (n) => String(n).padStart(2, '0');
    const h = Math.floor(remaining / 3600000);
    const m = Math.floor((remaining % 3600000) / 60000);
    const s = Math.floor((remaining % 60000) / 1000);
    const clock = h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
    this.textContent = this.template.replace('{timer}', clock);
  }
}

/* ------------------------------------------------------------------ *
 * Cart drawer cross-sell
 *
 * Delegated, because the drawer is re-rendered by the Section Rendering API on
 * every cart change and any listener bound to its buttons would be thrown away.
 * ------------------------------------------------------------------ */

async function addUpsell(variantId, button) {
  const root = window.Shopify?.routes?.root || '/';
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  try {
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: cartSectionIds().join(',') }),
    });
    if (!res.ok) throw new Error(await res.text());
    const payload = await res.json().catch(() => null);
    renderCartSections(payload?.sections);
    cartStore.refresh().then(announceCartUpdate);
  } catch (error) {
    console.error('[cro-upsell] add failed', error);
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-cro-upsell-add]');
  if (!button) return;
  event.preventDefault();
  addUpsell(button.dataset.croUpsellAdd, button);
});

/**
 * Close the cart drawer when the shopper clicks the backdrop. Horizon opens the
 * dialog with `closedby` at "none", so a modal backdrop click does nothing and
 * the drawer traps the shopper until they find the X. Delegated at document
 * level so it survives the drawer being re-rendered on every cart change.
 */
document.addEventListener('click', (event) => {
  const drawer = document.querySelector('#cart-drawer, cart-drawer');
  const dialog = drawer?.querySelector('dialog');
  if (!dialog?.open || !(event.target instanceof Element)) return;

  // Horizon opens the drawer as a NON modal dialog, so there is no backdrop to
  // catch and an outside click lands on the page like any other. Handle both:
  // a click that misses the drawer entirely, and a backdrop click if some theme
  // does open it modally. The trigger is excluded so opening it never re-closes.
  const hit = event.target.closest('#cart-drawer, cart-drawer, [aria-controls="cart-drawer"]');
  if (hit) {
    // Inside the panel the click is retargeted to the child that was hit, so
    // the dialog itself is only ever the target for a modal backdrop.
    if (event.target !== dialog) return;
    const box = dialog.getBoundingClientRect();
    const inside =
      event.clientX >= box.left &&
      event.clientX <= box.right &&
      event.clientY >= box.top &&
      event.clientY <= box.bottom;
    if (inside) return;
  }
  // Prefer the theme's own close so its state and animation stay in step.
  if (typeof drawer.close === 'function') drawer.close();
  else dialog.close();
});

if (!customElements.get('cro-packages')) customElements.define('cro-packages', CroPackages);
if (!customElements.get('cro-progress')) customElements.define('cro-progress', CroProgress);
if (!customElements.get('cro-countdown')) customElements.define('cro-countdown', CroCountdown);
